'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Zap, Brain, Shuffle, CheckCircle, XCircle } from 'lucide-react';
import type { ReactionTestMetrics, WorkingMemoryMetrics, FlexibilityMetrics } from '@/lib/services/brainCheckService';

// ─── Shared Types ─────────────────────────────────────────────────────────────

interface TestProps {
  onComplete: (metrics: any) => void;
}

// ─── Reaction & Attention Test ────────────────────────────────────────────────

const REACTION_STIMULI = 12;
const REACTION_GREEN_RATIO = 0.6; // 60% green (go), 40% red (no-go)

export function ReactionAttentionTest({ onComplete }: TestProps) {
  const [phase, setPhase] = useState<'intro' | 'active' | 'done'>('intro');
  const [stimulus, setStimulus] = useState<'green' | 'red' | null>(null);
  const [stimulusIdx, setStimulusIdx] = useState(0);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [errors, setErrors] = useState(0);
  const [misses, setMisses] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'error' | 'miss' | null>(null);
  const [countdown, setCountdown] = useState(3);
  const stimulusStart = useRef<number>(0);
  const stimulusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sequence = useRef<('green' | 'red')[]>([]);

  // Build randomised sequence
  useEffect(() => {
    const seq: ('green' | 'red')[] = [];
    for (let i = 0; i < REACTION_STIMULI; i++) {
      seq.push(Math.random() < REACTION_GREEN_RATIO ? 'green' : 'red');
    }
    sequence.current = seq;
  }, []);

  // Countdown before start
  useEffect(() => {
    if (phase !== 'intro') return;
    if (countdown <= 0) { setPhase('active'); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const showNextStimulus = useCallback(() => {
    if (stimulusIdx >= REACTION_STIMULI) {
      setPhase('done');
      return;
    }
    const delay = 800 + Math.random() * 1200; // 0.8–2s ISI
    stimulusTimeout.current = setTimeout(() => {
      setStimulus(sequence.current[stimulusIdx]);
      stimulusStart.current = Date.now();
      // Auto-miss after 1.2s
      stimulusTimeout.current = setTimeout(() => {
        if (sequence.current[stimulusIdx] === 'green') {
          setMisses(m => m + 1);
          setFeedback('miss');
        }
        setStimulus(null);
        setStimulusIdx(i => i + 1);
        setTimeout(() => setFeedback(null), 400);
      }, 1200);
    }, delay);
  }, [stimulusIdx]);

  useEffect(() => {
    if (phase === 'active') showNextStimulus();
    return () => { if (stimulusTimeout.current) clearTimeout(stimulusTimeout.current); };
  }, [phase, stimulusIdx, showNextStimulus]);

  useEffect(() => {
    if (phase === 'done') {
      const metrics: ReactionTestMetrics = {
        reactionTimes,
        errors,
        misses,
        totalStimuli: REACTION_STIMULI,
      };
      onComplete(metrics);
    }
  }, [phase]);

  const handleTap = () => {
    if (phase !== 'active' || stimulus === null) return;
    if (stimulusTimeout.current) clearTimeout(stimulusTimeout.current);
    const rt = Date.now() - stimulusStart.current;

    if (stimulus === 'green') {
      setReactionTimes(prev => [...prev, rt]);
      setFeedback('correct');
    } else {
      setErrors(e => e + 1);
      setFeedback('error');
    }
    setStimulus(null);
    setStimulusIdx(i => i + 1);
    setTimeout(() => setFeedback(null), 400);
  };

  const progress = Math.round((stimulusIdx / REACTION_STIMULI) * 100);

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="flex items-center gap-2 text-[#F97316]">
        <Zap size={18} />
        <span className="text-sm font-bold">Reaction & Attention</span>
      </div>

      {phase === 'intro' && (
        <div className="text-center space-y-4 max-w-xs">
          <div className="text-5xl font-bold text-white">{countdown > 0 ? countdown : '▶'}</div>
          <p className="text-sm text-zinc-400">
            <span className="text-emerald-400 font-semibold">Tap GREEN</span> — hold back on <span className="text-red-400 font-semibold">RED</span>
          </p>
          <p className="text-xs text-zinc-600">React as fast as you can. Avoid false taps.</p>
        </div>
      )}

      {phase === 'active' && (
        <div className="flex flex-col items-center gap-6 w-full max-w-xs">
          {/* Progress */}
          <div className="w-full bg-[#1E3A5F]/40 rounded-full h-1.5">
            <div className="h-1.5 rounded-full bg-[#F97316] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>

          {/* Stimulus area */}
          <button
            onClick={handleTap}
            className="w-full aspect-square max-w-[220px] rounded-3xl flex items-center justify-center transition-all duration-150 active:scale-95 select-none"
            style={{
              background: stimulus === 'green' ?'rgba(16,185,129,0.25)'
                : stimulus === 'red' ?'rgba(244,63,94,0.25)' :'rgba(30,58,95,0.3)',
              border: stimulus === 'green' ?'3px solid #10B981'
                : stimulus === 'red' ?'3px solid #F43F5E' :'3px solid rgba(30,58,95,0.5)',
            }}
            aria-label="Stimulus target"
          >
            {stimulus === 'green' && <div className="w-20 h-20 rounded-full bg-emerald-400 shadow-lg shadow-emerald-500/40" />}
            {stimulus === 'red' && <div className="w-20 h-20 rounded-full bg-red-400 shadow-lg shadow-red-500/40" />}
            {stimulus === null && <div className="w-4 h-4 rounded-full bg-[#1E3A5F]" />}
          </button>

          {/* Feedback */}
          <div className="h-6 flex items-center justify-center">
            {feedback === 'correct' && <span className="text-emerald-400 text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Correct!</span>}
            {feedback === 'error' && <span className="text-red-400 text-sm font-bold flex items-center gap-1"><XCircle size={14} /> False tap</span>}
            {feedback === 'miss' && <span className="text-amber-400 text-sm font-bold">Too slow</span>}
          </div>

          <p className="text-xs text-zinc-600">{stimulusIdx}/{REACTION_STIMULI} stimuli</p>
        </div>
      )}
    </div>
  );
}

// ─── Working Memory Test (2-Back) ─────────────────────────────────────────────

const WM_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const WM_TRIALS = 10;
const WM_DISPLAY_MS = 1500;
const WM_ISI_MS = 500;

export function WorkingMemoryTest({ onComplete }: TestProps) {
  const [phase, setPhase] = useState<'intro' | 'active' | 'done'>('intro');
  const [sequence, setSequence] = useState<string[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [currentLetter, setCurrentLetter] = useState<string | null>(null);
  const [responseTimes, setResponseTimes] = useState<number[]>([]);
  const [correct, setCorrect] = useState(0);
  const [total2Back, setTotal2Back] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'miss' | 'false_alarm' | null>(null);
  const [countdown, setCountdown] = useState(3);
  const letterStart = useRef<number>(0);
  const responded = useRef(false);

  useEffect(() => {
    const seq: string[] = [];
    for (let i = 0; i < WM_TRIALS + 2; i++) {
      if (i >= 2 && Math.random() < 0.35) {
        seq.push(seq[i - 2]); // 2-back match
      } else {
        let letter: string;
        do { letter = WM_LETTERS[Math.floor(Math.random() * WM_LETTERS.length)]; }
        while (i >= 2 && letter === seq[i - 2]);
        seq.push(letter);
      }
    }
    setSequence(seq);
  }, []);

  useEffect(() => {
    if (phase !== 'intro') return;
    if (countdown <= 0) { setPhase('active'); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'active') return;
    if (currentIdx >= sequence.length) { setPhase('done'); return; }

    responded.current = false;
    setCurrentLetter(sequence[currentIdx]);
    letterStart.current = Date.now();

    const hideTimer = setTimeout(() => {
      // If 2-back match and no response → miss
      if (currentIdx >= 2 && sequence[currentIdx] === sequence[currentIdx - 2] && !responded.current) {
        setTotal2Back(t => t + 1);
        setFeedback('miss');
        setTimeout(() => setFeedback(null), 400);
      }
      setCurrentLetter(null);
      setTimeout(() => setCurrentIdx(i => i + 1), WM_ISI_MS);
    }, WM_DISPLAY_MS);

    return () => clearTimeout(hideTimer);
  }, [phase, currentIdx, sequence]);

  useEffect(() => {
    if (phase === 'done') {
      const metrics: WorkingMemoryMetrics = {
        accuracy: total2Back > 0 ? correct / total2Back : 0,
        responseTimes,
        totalTrials: total2Back,
        correctTrials: correct,
      };
      onComplete(metrics);
    }
  }, [phase]);

  const handleMatch = () => {
    if (phase !== 'active' || currentIdx < 2 || responded.current) return;
    responded.current = true;
    const rt = Date.now() - letterStart.current;
    const isMatch = sequence[currentIdx] === sequence[currentIdx - 2];

    if (isMatch) {
      setCorrect(c => c + 1);
      setTotal2Back(t => t + 1);
      setResponseTimes(prev => [...prev, rt]);
      setFeedback('correct');
    } else {
      setFeedback('false_alarm');
    }
    setTimeout(() => setFeedback(null), 400);
  };

  const progress = Math.round((currentIdx / (sequence.length || 1)) * 100);

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="flex items-center gap-2 text-sky-400">
        <Brain size={18} />
        <span className="text-sm font-bold">Working Memory</span>
      </div>

      {phase === 'intro' && (
        <div className="text-center space-y-4 max-w-xs">
          <div className="text-5xl font-bold text-white">{countdown > 0 ? countdown : '▶'}</div>
          <p className="text-sm text-zinc-400">
            Tap <span className="text-sky-400 font-semibold">MATCH</span> when the letter matches the one from <span className="text-white font-semibold">2 steps ago</span>
          </p>
          <p className="text-xs text-zinc-600">Example: A → B → A ← tap here!</p>
        </div>
      )}

      {phase === 'active' && (
        <div className="flex flex-col items-center gap-6 w-full max-w-xs">
          <div className="w-full bg-[#1E3A5F]/40 rounded-full h-1.5">
            <div className="h-1.5 rounded-full bg-sky-400 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>

          <div className="w-32 h-32 rounded-2xl bg-[#0B1628] border-2 border-[#1E3A5F]/60 flex items-center justify-center">
            <span className="text-5xl font-bold text-white">{currentLetter ?? ''}</span>
          </div>

          <button
            onClick={handleMatch}
            className="w-full max-w-[220px] py-5 rounded-2xl bg-sky-500/15 border-2 border-sky-500/40 text-sky-400 font-bold text-lg active:scale-95 transition-all select-none"
            aria-label="Match button"
          >
            MATCH
          </button>

          <div className="h-6 flex items-center justify-center">
            {feedback === 'correct' && <span className="text-emerald-400 text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Correct!</span>}
            {feedback === 'miss' && <span className="text-amber-400 text-sm font-bold">Missed it</span>}
            {feedback === 'false_alarm' && <span className="text-red-400 text-sm font-bold flex items-center gap-1"><XCircle size={14} /> Not a match</span>}
          </div>

          <p className="text-xs text-zinc-600">{currentIdx}/{sequence.length} letters</p>
        </div>
      )}
    </div>
  );
}

// ─── Cognitive Flexibility Test ───────────────────────────────────────────────

type FlexRule = 'colour' | 'shape';
type FlexColour = 'red' | 'blue';
type FlexShape = 'circle' | 'square';

interface FlexStimulus {
  colour: FlexColour;
  shape: FlexShape;
  rule: FlexRule;
  isSwitch: boolean;
}

const FLEX_TRIALS = 10;

function buildFlexSequence(): FlexStimulus[] {
  const colours: FlexColour[] = ['red', 'blue'];
  const shapes: FlexShape[] = ['circle', 'square'];
  const seq: FlexStimulus[] = [];
  let prevRule: FlexRule = 'colour';

  for (let i = 0; i < FLEX_TRIALS; i++) {
    const isSwitch = i > 0 && Math.random() < 0.4;
    const rule: FlexRule = isSwitch ? (prevRule === 'colour' ? 'shape' : 'colour') : prevRule;
    seq.push({
      colour: colours[Math.floor(Math.random() * 2)],
      shape: shapes[Math.floor(Math.random() * 2)],
      rule,
      isSwitch,
    });
    prevRule = rule;
  }
  return seq;
}

export function CognitiveFlexibilityTest({ onComplete }: TestProps) {
  const [phase, setPhase] = useState<'intro' | 'active' | 'done'>('intro');
  const [sequence] = useState<FlexStimulus[]>(() => buildFlexSequence());
  const [trialIdx, setTrialIdx] = useState(0);
  const [switchingLatencies, setSwitchingLatencies] = useState<number[]>([]);
  const [stayLatencies, setStayLatencies] = useState<number[]>([]);
  const [postSwitchErrors, setPostSwitchErrors] = useState(0);
  const [totalSwitches, setTotalSwitches] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'error' | null>(null);
  const [countdown, setCountdown] = useState(3);
  const trialStart = useRef<number>(0);

  useEffect(() => {
    if (phase !== 'intro') return;
    if (countdown <= 0) { setPhase('active'); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase === 'active') {
      trialStart.current = Date.now();
    }
  }, [phase, trialIdx]);

  useEffect(() => {
    if (phase === 'done') {
      const metrics: FlexibilityMetrics = {
        switchingLatencies,
        stayLatencies,
        postSwitchErrors,
        totalSwitches,
      };
      onComplete(metrics);
    }
  }, [phase]);

  const handleResponse = (choice: string) => {
    if (phase !== 'active') return;
    const trial = sequence[trialIdx];
    const rt = Date.now() - trialStart.current;

    const correctAnswer = trial.rule === 'colour' ? trial.colour : trial.shape;
    const isCorrect = choice === correctAnswer;

    if (trial.isSwitch) {
      setTotalSwitches(t => t + 1);
      if (isCorrect) setSwitchingLatencies(prev => [...prev, rt]);
      else setPostSwitchErrors(e => e + 1);
    } else {
      if (isCorrect) setStayLatencies(prev => [...prev, rt]);
    }

    setFeedback(isCorrect ? 'correct' : 'error');
    setTimeout(() => {
      setFeedback(null);
      if (trialIdx + 1 >= FLEX_TRIALS) setPhase('done');
      else setTrialIdx(i => i + 1);
    }, 500);
  };

  const trial = sequence[trialIdx];
  const progress = Math.round((trialIdx / FLEX_TRIALS) * 100);

  const colourHex: Record<FlexColour, string> = { red: '#F43F5E', blue: '#38BDF8' };

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="flex items-center gap-2 text-purple-400">
        <Shuffle size={18} />
        <span className="text-sm font-bold">Cognitive Flexibility</span>
      </div>

      {phase === 'intro' && (
        <div className="text-center space-y-4 max-w-xs">
          <div className="text-5xl font-bold text-white">{countdown > 0 ? countdown : '▶'}</div>
          <p className="text-sm text-zinc-400">
            Sort by <span className="text-purple-400 font-semibold">COLOUR</span> or <span className="text-purple-400 font-semibold">SHAPE</span> — the rule changes!
          </p>
          <div className="flex gap-3 justify-center text-xs text-zinc-500">
            <span>🔴 Red / 🔵 Blue</span>
            <span>⬤ Circle / ■ Square</span>
          </div>
        </div>
      )}

      {phase === 'active' && trial && (
        <div className="flex flex-col items-center gap-5 w-full max-w-xs">
          <div className="w-full bg-[#1E3A5F]/40 rounded-full h-1.5">
            <div className="h-1.5 rounded-full bg-purple-400 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>

          {/* Rule indicator */}
          <div className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${
            trial.rule === 'colour' ?'bg-amber-950/30 border-amber-700/40 text-amber-400' :'bg-purple-950/30 border-purple-700/40 text-purple-400'
          }`}>
            {trial.isSwitch && <span className="mr-1">⚡</span>}
            Sort by {trial.rule.toUpperCase()}
          </div>

          {/* Stimulus */}
          <div className="w-28 h-28 flex items-center justify-center">
            {trial.shape === 'circle'
              ? <div className="w-20 h-20 rounded-full border-4" style={{ borderColor: colourHex[trial.colour], background: `${colourHex[trial.colour]}20` }} />
              : <div className="w-20 h-20 rounded-lg border-4" style={{ borderColor: colourHex[trial.colour], background: `${colourHex[trial.colour]}20` }} />
            }
          </div>

          {/* Response buttons */}
          {trial.rule === 'colour' ? (
            <div className="grid grid-cols-2 gap-3 w-full">
              <button onClick={() => handleResponse('red')} className="py-4 rounded-2xl bg-red-500/15 border-2 border-red-500/40 text-red-400 font-bold active:scale-95 transition-all">RED</button>
              <button onClick={() => handleResponse('blue')} className="py-4 rounded-2xl bg-sky-500/15 border-2 border-sky-500/40 text-sky-400 font-bold active:scale-95 transition-all">BLUE</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 w-full">
              <button onClick={() => handleResponse('circle')} className="py-4 rounded-2xl bg-purple-500/15 border-2 border-purple-500/40 text-purple-400 font-bold active:scale-95 transition-all">⬤ CIRCLE</button>
              <button onClick={() => handleResponse('square')} className="py-4 rounded-2xl bg-purple-500/15 border-2 border-purple-500/40 text-purple-400 font-bold active:scale-95 transition-all">■ SQUARE</button>
            </div>
          )}

          <div className="h-6 flex items-center justify-center">
            {feedback === 'correct' && <span className="text-emerald-400 text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Correct!</span>}
            {feedback === 'error' && <span className="text-red-400 text-sm font-bold flex items-center gap-1"><XCircle size={14} /> Wrong rule</span>}
          </div>

          <p className="text-xs text-zinc-600">{trialIdx + 1}/{FLEX_TRIALS} trials</p>
        </div>
      )}
    </div>
  );
}
